import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import axiosInstance from './axios';

// API/Components
import {
	ANALYSIS_RESULT,
	ANALYSIS_TYPE,
	DATA_ERROR_VALUE,
	GRAPH_BOUNDS,
	GRAPH_STYLE,
	isEmpty,
	validValue,
	AnalysisResultToStateColor,
	formatDateTime,
	measUnitsConversion,
	measUnitsArrayConversion,
	getUserTempUnits
} from './enums';
import { DrawVerticalDashedLineAtTooltip } from './chart_helpers';
import {
	createUnitGraphPopover,
} from './unit_graph_helpers';

// Material UI
import Button from '@mui/material/Button';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import Grid from '@mui/material/Grid';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import tinycolor from 'tinycolor2';
import 'chartjs-adapter-date-fns';

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
	zoomPlugin,
);


export default function UnitDischargeTrendGraph(props) {
	const { trendData, limitData, graphScalesData, selectedString, selectedUnit, graphType, loading } = props;

	const { locationNum, eventDateTime } = useParams();
	
	const chartRef = useRef(null);
	
	const [graphData, setGraphData] = useState([]);
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
		maxminEnabled: false,
		avgEnabled: false,
		ambientEnabled: true,
		initialsEnabled: true,
	});
	const [graphStyle, setGraphStyle] = useState(GRAPH_STYLE.AREA);
	
	const [maxAvgMinData, setMaxAvgMinData] = useState({});
	const [graphOptions, setGraphOptions] = useState({
		maintainAspectRatio: false,
		animation: false,
		options: {
			responsive: true,
		},
		transitions: {
			zoom: {
				animation: {
					duration: 0,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				intersect: false,
				mode: 'index',
				...createUnitGraphPopover(),
			},
			zoom: {
				pan: {
					enabled: true,
					mode: 'xy',
				},
				limits: {
					x: {
						minRange: 2,
					},
					y: {
						min: 0,
						max: 100,
						minRange: 5,
					}
				},
				zoom: {
					mode: 'y',
					wheel: {
						enabled: true,
						speed: 0.02,
					},
					pinch: {
						enabled: true,
					},
					onZoomStart: (e) => {
						if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
							return;
						}
						if (e.event.shiftKey === true) {
							e.chart.config.options.plugins.zoom.zoom.mode = "x";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
						} else {
							e.chart.config.options.plugins.zoom.zoom.mode = "y";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
						}
					},
				}
			},
		},
	});
	
	// const [isLoading, setIsLoading] = useState(loading);

	// Keep track of the graph type for drawing limit lines
	const graphTypeRef = useRef(null);
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Get the list of max/avg/min data for the specified location id and string number
	useEffect(() => {
		if (selectedString !== undefined || selectedUnit !== undefined) {
			axiosInstance.get(`/loc/${locationNum}/discharges/${eventDateTime}/stats_flat/string_${selectedString}`).then(response => {
				setMaxAvgMinData(response.data);
			});
		}
	}, [locationNum, selectedString]);

	// Update the graph data sets
	useEffect(() => {
		if (typeof trendData != "undefined" && trendData !== null && !isEmpty(trendData)) {
			const unitsData = GetGraphData(trendData.measurements, graphType);
			if (unitsData !== undefined && unitsData !== null) {
				setGraphData(unitsData);
			}
		}
	}, [trendData, selectedString, selectedUnit, graphType, graphSettings, graphStyle, limitData]);

	useEffect(() => {
		const graphScales = SetGraphYScale(graphType);
		if (graphScales !== undefined && graphScales !== null && !isEmpty(trendData)) {
			
			const firstDate = formatDateTime(trendData.measurements[0].datetime);
			const lastDate = formatDateTime(trendData.measurements[trendData.measurements.length - 1].datetime);
			
			setGraphOptions((prevState) => {
				return {
					...prevState,
					scales: {
						y: graphScales,
						x: {
							title: {
								display: false,
							},
							suggestedMin: firstDate,
							suggestedMax: lastDate,
						}
					}
				}
			});
		}

		SetGraphBounds();
	}, [graphScalesData, graphType]);
	
	const graphPlugins = [{
		afterDraw: chart => DrawVerticalDashedLineAtTooltip(chart),
	}];

	function SetGraphBounds() {
		let graphZoom = GRAPH_BOUNDS.UNIT_DISCHARGE_VOLTAGE;
		switch(graphType) {
			case 0:  // Voltage
				graphZoom = GRAPH_BOUNDS.UNIT_DISCHARGE_VOLTAGE;
				break;

			case 1:  // Temperature
				graphZoom = {
					...GRAPH_BOUNDS.TEMPERATURE,
					bounds_max: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_max),
					bounds_min: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_min),
				};
				break;

			default:
				graphZoom = GRAPH_BOUNDS.UNIT_DISCHARGE_VOLTAGE;
				break;
		}
		
		setGraphOptions((prevState) => {
			return {
				...prevState,
				plugins: {
					...prevState.plugins,
					zoom: {
						...prevState.plugins.zoom,
						limits: {
							...prevState.plugins.zoom.limits,
							y: {
								max: graphZoom.bounds_max,
								min: graphZoom.bounds_min,
								minRange: graphZoom.min_range
							},
						},
					},
				},
			}
		});
	}

	function GetLimitsData(limitData, graphType) {
		let critHiLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		let maintLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		let critLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;

		// Find the limits for the selected unit
		const unitLimits = limitData.units.find(unit => unit.str_nbr === selectedString && unit.unit_nbr === selectedUnit);
		
		if (limitData != undefined && limitData != null && !isEmpty(limitData) &&
			graphType != undefined && graphType != null) {
			switch(graphType) {
				case 0:
					maintLoLimit = unitLimits.volt.lo_maint;
					critLoLimit = unitLimits.volt.lo_crit;
					break;
				case 1:
					critHiLimit = measUnitsConversion(limitData.system.temp_hi);
					critLoLimit = measUnitsConversion(limitData.system.temp_lo);
					break;
				default:
					maintLoLimit = unitLimits.volt.lo_maint;
					critLoLimit = unitLimits.volt.lo_crit;
					break;
			}
		}

		// Get the associated limit colors
		let hiCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL);
		let loMaintColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS);
		let loCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL);

		let limitSets = [];
		if (critHiLimit !== undefined && critHiLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				label: 'Crit High',
				value: critHiLimit,
				color: hiCritColor
			});
		}

		if (maintLoLimit !== undefined && maintLoLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				label: 'Maint Low',
				value: maintLoLimit,
				color: loMaintColor
			});
		}

		if (critLoLimit !== undefined && critLoLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				label: 'Crit Low',
				value: critLoLimit,
				color: loCritColor
			});
		}
		
		return limitSets;
	}

	function GetMaxAvgMinData(graphType) {
		let maxValues = [];
		let avgValues = [];
		let minValues = [];
		
		if (maxAvgMinData !== undefined && maxAvgMinData !== null && !isEmpty(maxAvgMinData)) {
			switch(graphType) {
				case 0:
					maxValues = maxAvgMinData.volt_max;
					avgValues = maxAvgMinData.volt_avg;
					minValues = maxAvgMinData.volt_min;
					break;
				case 1:
					maxValues = measUnitsArrayConversion(maxAvgMinData.temp_max);
					avgValues = measUnitsArrayConversion(maxAvgMinData.temp_avg);
					minValues = measUnitsArrayConversion(maxAvgMinData.temp_min);
					break;
				default:
					maxValues = [];
					avgValues = [];
					minValues = [];
					break;
			}
		}
		
		// Get the associated limit colors
		let maxColor = '#0033FF';
		let avgColor = '#005500';
		let minColor = '#0033FF';

		let statSets = {
			'maxSet': {},
			'avgSet': {},
			'minSet': {}
		};
		if (maxValues !== undefined && maxValues.length > 0) {
			statSets.maxSet = {
				type: 'scatter',
				label: 'String Max',
				backgroundColor: maxColor + "55",
				borderColor: maxColor + "77",
				borderWidth: 1,
				pointStyle: 'triangle',
				radius: 4,
				rotation: 180,
				hitRadius: 5,
				data: maxValues,
				order: 4,
			};
		}
		
		if (avgValues !== undefined && avgValues.length > 0) {
			statSets.avgSet = {
				type: 'scatter',
				label: 'String Avg',
				backgroundColor: avgColor + "55",
				borderColor: avgColor + "77",
				borderWidth: 1,
				pointStyle: 'crossRot',
				radius: 5,
				hitRadius: 5,
				data: avgValues,
				order: 5,
			};
		}

		if (minValues !== undefined && minValues.length > 0) {
			statSets.minSet = {
				type: 'scatter',
				label: 'String Min',
				backgroundColor: minColor + "55",
				borderColor: minColor + "77",
				borderWidth: 1,
				pointStyle: 'triangle',
				radius: 4,
				rotation: 0,
				hitRadius: 5,
				data: minValues,
				order: 6,
			};
		}

		return statSets;
	}

	function SetGraphYScale(graphType) {
			
		// Find the graph scales for the selected unit
		const unitScale = graphScalesData.find(unit => unit.string_nbr === selectedString && unit.unit_nbr === selectedUnit);

		if (unitScale !== undefined && unitScale !== null) {

			let max = DATA_ERROR_VALUE.INVALID_VALUE;
			let min = DATA_ERROR_VALUE.INVALID_VALUE;
			let title = "";
			
			switch (graphType) {
				case 0:  // Voltage
					max = validValue(unitScale.voltage?.max, GRAPH_BOUNDS.UNIT_DISCHARGE_VOLTAGE.default_max);
					min = validValue(unitScale.voltage?.discharge_min, GRAPH_BOUNDS.UNIT_DISCHARGE_VOLTAGE.default_min);
					title = `Unit Voltage (${ANALYSIS_TYPE.VOLTAGE.unit})`;
					break;

				case 1:  // Temperature
					const maxC = validValue(unitScale.temperature?.max, GRAPH_BOUNDS.TEMPERATURE.default_max);
					const minC = validValue(unitScale.temperature?.min, GRAPH_BOUNDS.TEMPERATURE.default_min);
					max = measUnitsConversion(maxC);
					min = measUnitsConversion(minC);
					title = `Unit Temperature (${getUserTempUnits()})`;
					break;

				default:  // Voltage
					max = validValue(unitScale.voltage?.max, GRAPH_BOUNDS.UNIT_DISCHARGE_VOLTAGE.default_max);
					min = validValue(unitScale.voltage?.discharge_min, GRAPH_BOUNDS.UNIT_DISCHARGE_VOLTAGE.default_min);
					title = `Unit Voltage (${ANALYSIS_TYPE.VOLTAGE.unit})`;
					break;
			}

			if (max !== DATA_ERROR_VALUE.INVALID_VALUE && min !== DATA_ERROR_VALUE.INVALID_VALUE) {
				return {
					max: max,
					min: min,
					title: {
						display: true,
						text: title,
						font: {
							family: "Saira, sans-serif",
							size: 18,
							weight: 600,
						},
					},
				};
			}
		}

		return {};
	}

	function GetUnitInfo(measurement, graphType) {
		let value, analysis = 0;
		switch(graphType) {
			case 0:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ? 
					measurement.voltage.analysis_result : 0;
				break;
			case 1:
				let temp = measurement.temperature.measurement;
				value = measUnitsConversion(temp)?.toFixed(1);
				analysis = (measurement.temperature.analysis_valid) ? 
					measurement.temperature.analysis_result : 0;
				break;
			default:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ?
					measurement.voltage.analysis_result : 0;
			break;
		}
		value = (value !== null && value !== undefined && value > -4000000) ? value : 0;
		let analysisColor = AnalysisResultToStateColor(analysis);

		let color = measurement.unit_replaced ? tinycolor(analysisColor).darken(25).toString() : analysisColor;

		return {value, color};
	}

	function GetGraphTypeText(graphType) {
		switch(graphType) {
			case 0:
				return "Unit Voltage";
			case 1:
				return "Unit Temperature";
			default:
				return "Unit Voltage";
		}
	}
	
	function GetGraphData(measurementsTrendData, graphType) {
		// Pick the units to graph based on the selected string
		if (measurementsTrendData && measurementsTrendData.length > 0) {
			// Get the measurement data for the specified measurement data and graph type
			function GetSingleTrendData(measurementData, graphType, graphStyle, colorOverride = null) {
	
				let barColor = [];
				let borderColor = [];
				let chartData = [];
	
				const graphTypeText = GetGraphTypeText(graphType);
	
				measurementData.forEach((measurement) => {
					const { value, color } = GetUnitInfo(measurement, graphType);
					
					let setColor = colorOverride !== null ? colorOverride : color;
					let label = formatDateTime(measurement.datetime);

					if (value !== DATA_ERROR_VALUE.INVALID_VALUE) {
						barColor.push(setColor + "77");
						borderColor.push(setColor);
						chartData.push({x: label, y: value});
					}
				});

				return {
					type: graphStyle === GRAPH_STYLE.POINT ? 'scatter' : 'line',
					label: graphTypeText,
					backgroundColor: barColor,
					borderColor: borderColor,
					segment: {
						borderColor: (ctx) => (barColor[ctx.p1DataIndex]),
						backgroundColor: (ctx) => (barColor[ctx.p1DataIndex]),
					},
					data: chartData,
					fill: graphStyle === GRAPH_STYLE.AREA,
					order: 10,
				}
			}
	
			let dataSets = [];
			dataSets.push(GetSingleTrendData(measurementsTrendData, graphType, graphStyle));

			// Add dashed horizontal limit lines as lightweight datasets so they appear
			// as standard tooltip rows with native color markers.
			if (graphSettings.limitsEnabled && limitData !== undefined && limitData !== null && !isEmpty(limitData)) {
				const limitsData = GetLimitsData(limitData, graphType);
				if (limitsData !== undefined && limitsData !== null && limitsData.length > 0) {
					const limitDataSets = limitsData.map((limit) => ({
						type: 'line',
						label: limit.label,
						data: measurementsTrendData.map(() => limit.value),
						borderColor: limit.color,
						backgroundColor: limit.color + '77',
						borderWidth: 2,
						borderDash: [6, 4],
						pointRadius: 0,
						pointHoverRadius: 0,
						pointHitRadius: 0,
						fill: false,
						spanGaps: true,
						order: 0,
					}));

					dataSets.push(...limitDataSets);
				}
			}


			// check for max/min/avg enabled and add data to datasets if enabled
			if (graphSettings.maxminEnabled || graphSettings.avgEnabled) {
				const maxavgminData = GetMaxAvgMinData(graphType);
				if (maxavgminData !== undefined && !isEmpty(maxavgminData)) {
					if (graphSettings.maxminEnabled) {
						if (maxavgminData.maxSet !== undefined) { dataSets.push(maxavgminData.maxSet); }
						if (maxavgminData.minSet !== undefined) { dataSets.push(maxavgminData.minSet); }
					}

					if (graphSettings.avgEnabled && maxavgminData.minSet !== undefined && maxavgminData.minSet != {}) {
						dataSets.push(maxavgminData.avgSet);
					}
				}
			}

			// If ambient temperature is enabled and this is the temperature graph, add ambient temperature data to datasets
			if (graphType === 1 && graphSettings.ambientEnabled) {
				const ambientData = measurementsTrendData.map((measurement) => {
					const ambientTemp = measurement?.system?.ambient_temp;
					if (ambientTemp === undefined || ambientTemp === null || ambientTemp <= DATA_ERROR_VALUE.INVALID_VALUE) {
						return null;
					}

					return measUnitsConversion(ambientTemp)?.toFixed(1);
				});

				if (ambientData.some((temp) => temp !== null && temp !== undefined)) {
					dataSets.push({
						type: 'line',
						label: 'Ambient Temperature',
						data: ambientData,
						borderColor: 'rgb(35, 141, 193)',
						backgroundColor: 'rgba(35, 141, 193, 0.7)',
						pointRadius: 1,
						hitRadius: 20,
						borderWidth: 2,
						tension: 0.15,
						fill: false,
						spanGaps: true,
						order: 11,
					});
				}
			}
	
			return {
				labels: measurementsTrendData.map((measurement) => formatDateTime(measurement.datetime)),
				datasets: dataSets,
			}
		}
	
		return null;
	}

	const handleLimitMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			limitsEnabled: !graphSettings.limitsEnabled})
		);
	};

	const handleMaxMinMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			maxminEnabled: !graphSettings.maxminEnabled})
		);
	};

	const handleAvgMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			avgEnabled: !graphSettings.avgEnabled})
		);
	};

	const handleAmbientMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			ambientEnabled: !graphSettings.ambientEnabled})
		);
	};

	const handleGraphResetZoom = () => {
		chartRef.current.resetZoom();
	};

	const handleGraphStyle = (event, newStyle) => {
		if (newStyle !== null) {
			setGraphStyle(newStyle);
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '99%',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
			}}
		>
			<div style={{ height: '100%', width: '100%' }}>
				{ (graphData !== undefined && graphData.length !== 0) &&
					<Chart
						ref={chartRef}
						type='line'
						options={graphOptions}
						data={graphData}
						plugins = {graphPlugins}
					/>
				}
			</div>
			<Grid container spacing={1}>
				<Grid item xs alignItems="left">
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value="limitMarkers"
							selected={graphSettings.limitsEnabled}
							onClick={handleLimitMarkersToggle}
						>
							Limits
						</ToggleButton>
						<ToggleButton
							value="maxminMarkers"
							selected={graphSettings.maxminEnabled}
							onClick={handleMaxMinMarkersToggle}
						>
							Max/Min
						</ToggleButton>
						<ToggleButton
							value="avgMarkers"
							selected={graphSettings.avgEnabled}
							onClick={handleAvgMarkersToggle}
						>
							Avg
						</ToggleButton>
						{ graphType === 1 &&
							<ToggleButton
								value="ambientMarkers"
								selected={graphSettings.ambientEnabled}
								onClick={handleAmbientMarkersToggle}
							>
								Ambient
							</ToggleButton>
						}
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs alignItems="center">
					<Button
						variant="contained"
						startIcon={<CenterFocusStrongIcon />}
						onClick={handleGraphResetZoom}
						sx={{
							marginY: 1,
							backgroundColor: 'white',
							border: '1px solid lightgray',
							color: 'black',
							":hover": {
								backgroundColor: 'lightgray',
								boxShadow: 'none',
							}

						}}
					>
						Reset View
					</Button>
				</Grid>
				<Grid item>
					<ToggleButtonGroup
						value={graphStyle}
						onChange={handleGraphStyle}
						exclusive
						aria-label="graph marker type"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value={GRAPH_STYLE.AREA}
							aria-label="Area Graph Style"
						>
							Area
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.LINE}
							aria-label="Line Graph Style"
						>
							Line
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.POINT}
							aria-label="Point Graph Style"
						>
							Point
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
			</Grid>
		</div>
	);
}